import { useMemo, useEffect } from 'react'
import { LayoutProps } from './types'
import { ClubDetailView } from '../ClubDetailView'
import { BulkUploadModal } from '../BulkUploadModal'
import { ClubGridWithTools } from './ClubGridWithTools'

export const ModalLayout = ({
  filteredAndSortedClubs,
  totalClubs,
  selectedClubId,
  onSelectClub,
  onDeselectClub,
  searchTerm,
  onSearchChange,
  sortValue,
  onSortChange,
  showBulkUpload,
  onShowBulkUpload,
}: LayoutProps) => {
  const selectedClub = useMemo(
    () => filteredAndSortedClubs.find((c) => c.clubId === selectedClubId),
    [filteredAndSortedClubs, selectedClubId]
  )

  const isOpen = !!selectedClub

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDeselectClub()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onDeselectClub])

  return (
    <>
      <ClubGridWithTools
        filteredAndSortedClubs={filteredAndSortedClubs}
        totalClubs={totalClubs}
        selectedClubId={selectedClubId}
        onSelectClub={onSelectClub}
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        sortValue={sortValue}
        onSortChange={onSortChange}
        onShowBulkUpload={onShowBulkUpload}
      />

      {isOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-1.5 sm:p-2">
          <div
            className="absolute inset-0 bg-black bg-opacity-40"
            onClick={onDeselectClub}
          />

          <div
            className="relative bg-white shadow-2xl w-[95vw] sm:w-[85vw] md:w-[75vw] lg:w-[60vw] xl:w-[55vw] 2xl:w-[50vw] flex flex-col"
            style={{
              height: 'calc(100vh - 0.75rem)',
              maxHeight: 'calc(100vh - 0.75rem)',
              animation: 'modalFadeIn 0.2s ease-out',
            }}
          >
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 px-5 py-2.5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">
                {selectedClub?.clubName}
              </h2>
              <button
                onClick={onDeselectClub}
                className="p-1 hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-auto p-4">
              {selectedClub && <ClubDetailView club={selectedClub} />}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <BulkUploadModal
        open={showBulkUpload}
        onClose={() => onShowBulkUpload(false)}
        onSuccess={() => onShowBulkUpload(false)}
      />
    </>
  )
}
